import { css, html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { typographyStyles } from '~/components/__utils__/component-utils';
import '~/components/button/button';

export type DialogProps = {
  title: string;
  hideCloseButton: boolean;
};

@customElement('dvg-dialog')
export class Dialog extends LitElement implements DialogProps {

  @query('#dvg-modal-dialog') modalDialog!: HTMLDialogElement;

  @property({ type: String }) title = '';
  @property({ type: Boolean, attribute: 'hide-close-button' }) hideCloseButton = false;

  private handleOverlayClick = (event: MouseEvent) => {
    if (event.target === this.shadowRoot?.querySelector('dialog')) {
      this.close();
    }
  }

  private handleEscapeKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', this.handleEscapeKey);
    window.addEventListener('click', this.handleOverlayClick);
  }

  firstUpdated() {
    const slottedActions = this._slottedActionsChildren;
    if (slottedActions.length > 0) {
      (slottedActions[0] as HTMLDivElement).classList.add('dvg-dialog-footer__actions-bar');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this.handleEscapeKey);
    window.removeEventListener('click', this.handleOverlayClick);
  }

  get _slottedActionsChildren() {
    return this.shadowRoot?.querySelector<HTMLSlotElement>("slot[name='dialog-actions']")?.assignedNodes({ flatten: false }) || [];
  }

  public show() {
    this.modalDialog.showModal();
  }

  public close() {
    this.modalDialog.close();
  }

  private renderDialogTitle() {
    if (!this.title) return null;

    return html`
      <p class="dialog-header__title">${this.title}</p>
    `;
  }

  private renderCloseButton() {
    if (this.hideCloseButton) return null;

    return html`
      <dvg-button variant="outlined" size="micro" class="dialog-header__close-button" @click="${this.close}" aria-label="Sluit dialoogvenster">
        <dvg-icon name="cross"></dvg-icon>
      </dvg-button>
    `;
  }

  render() {
    return html`
      <dialog id="dvg-modal-dialog" class="dialog">
        <div class="dialog-header">
          ${this.renderDialogTitle()}
          ${this.renderCloseButton()}
        </div>
        
        <div class="dialog-content">
          <slot></slot>
        </div>
        
        <div class="dialog-footer">
          <slot name="dialog-actions">
            <dvg-button theme="secondary" size="small" @click="${this.close}">Sluiten</dvg-button>
          </slot>
        </div>
      </dialog>
    `;
  }

  static styles = [typographyStyles, css`
    :host {
      --dvg-modal-background: rgba(251, 252, 252, 1);
      
      display: block;
      box-sizing: border-box;
    }
    *, *:before, *:after {
      box-sizing: inherit;
    }
    
    ::backdrop {
      background: rgb(0 0 0 / 0.5);
    }
    
    dialog {
      background: var(--dvg-modal-background);
      border: 1px solid var(--dvg-grey-light);
      border-radius: 24px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      padding: 1rem;
      
      width: clamp(40rem, 50vw, 60rem);
    }
    
    .dialog-header {
      border-bottom: var(--dvg-grey-light) 2px solid;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 0.5rem;
    }
    
    .dialog-header__close-button {
      align-self: center;
    }
    
    .dialog-header__title {
      font-family: 'Fraunces', serif;
      font-size: 2rem;
      font-weight: 300;
      margin: 0;
    }
    
    .dialog-content {
      margin-top: 1rem;
    }
    
    .dialog-footer {
      margin-top: 2rem;
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
    }
  `];
}

declare global {
  interface HTMLElementTagNameMap {
    'dvg-dialog': Dialog
  }
}
